import { useState, Fragment } from 'react';
import { ArbitrageOpportunity } from '../types';
import BookmakerLogo from './BookmakerLogo';
import { formatOdds as formatOddsUtil, type OddsFormat } from '../utils/oddsFormat';
import { getSafeExternalUrl } from '../utils/url';

interface OpportunityTableProps {
  opportunities: ArbitrageOpportunity[];
  maxBetSize: number;
  roundBets: boolean;
  calculateBetAmounts: (opportunity: ArbitrageOpportunity, totalBankroll: number, shouldRound: boolean) => { betAmounts: number[]; profit: number };
  oddsFormat: OddsFormat;
  userTier?: 'free' | 'gold' | 'platinum';
}

export default function OpportunityTable({
  opportunities,
  maxBetSize,
  roundBets,
  calculateBetAmounts,
  oddsFormat,
  userTier = 'free'
}: OpportunityTableProps) {
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 0) {
      return 'LIVE';
    }

    // Check if today
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return `Today at ${date.toLocaleString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })}`;
    }

    // Otherwise show full date
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatOdds = (odds: number) => {
    // The API always returns decimal odds, so we convert to the selected format for display
    return formatOddsUtil(odds, oddsFormat, 'decimal');
  };

  const formatBetAmount = (amount: number) => {
    if (amount === Math.floor(amount)) {
      return amount.toFixed(0);
    }
    return amount.toFixed(2);
  };

  const isLive = (dateString: string) => {
    return new Date(dateString) < new Date();
  };

  const formatSportLeague = (sportKey: string, sportTitle?: string) => {
    // Extract sport from sport_key (e.g., "basketball_nba" -> "Basketball")
    const sportPart = sportKey.split('_')[0];
    const sport = sportPart.charAt(0).toUpperCase() + sportPart.slice(1);

    // Use sport_title as league if available, otherwise use the full sport_key
    const league = sportTitle || sportKey;

    return `${sport} | ${league}`;
  };

  const formatMarket = (marketKey: string) => {
    if (marketKey === 'h2h') {
      return 'Moneyline';
    }
    // Capitalize first letter of each word
    return marketKey.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const toggleRow = (opportunityId: string) => {
    setExpandedRowId(expandedRowId === opportunityId ? null : opportunityId);
  };

  return (
    <div className="opportunities-wrapper">
      <table className="opportunities-table">
        <thead>
          <tr>
            <th></th>
            <th>ARB %</th>
            <th>EVENT</th>
            <th>MARKET</th>
            <th>BOOKS & BETS</th>
            <th>BET SIZE</th>
            <th>PROFIT</th>
          </tr>
        </thead>
        <tbody>
          {opportunities.map((opportunity) => {
            const { betAmounts, profit } = calculateBetAmounts(opportunity, maxBetSize, roundBets);
            const isExpanded = expandedRowId === opportunity.id;

            return (
              <Fragment key={opportunity.id}>
                <tr
                  onClick={() => toggleRow(opportunity.id)}
                  style={{ cursor: 'pointer' }}
                  className={isExpanded ? 'expanded-row' : ''}
                >
                  {/* Expand Indicator */}
                  <td style={{ width: '30px', textAlign: 'center' }}>
                    <span style={{
                      display: 'inline-block',
                      transition: 'transform 0.2s',
                      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'
                    }}>
                      ▶
                    </span>
                  </td>

                  {/* ARB % */}
                  <td className="table-cell-profit">
                    +{opportunity.profit_percentage.toFixed(2)}%
                  </td>

                {/* EVENT */}
                <td>
                  {/* Date/Time Line */}
                  <div className="table-cell-meta" style={{ marginBottom: '4px' }}>
                    {isLive(opportunity.commence_time) ? (
                      <span style={{ color: '#4da3ff', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span style={{
                          display: 'inline-block',
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          backgroundColor: '#4da3ff'
                        }}></span>
                        LIVE
                      </span>
                    ) : (
                      formatDate(opportunity.commence_time)
                    )}
                  </div>
                  {/* Teams Line */}
                  <div className="table-cell-event" style={{ marginBottom: '4px' }}>
                    {opportunity.away_team} vs {opportunity.home_team}
                  </div>
                  {/* Sport | League Line */}
                  <div className="table-cell-event">
                    {formatSportLeague(opportunity.sport_key, opportunity.sport_title)}
                  </div>
                </td>

                {/* MARKET */}
                <td>
                  <span className="table-market-text">
                    {formatMarket(opportunity.market_key)}
                  </span>
                </td>

                {/* BOOKS & BETS */}
                <td>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {opportunity.best_odds.map((bet) => {
                      const safeLink = getSafeExternalUrl(bet.link);

                      return (
                      <div key={`${bet.bookmaker_key}-${bet.outcome_name}`} style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                          <BookmakerLogo
                            bookmakerKey={bet.bookmaker_key}
                            bookmakerTitle={bet.bookmaker_title}
                          />
                          <span className="table-cell-odds" style={{ minWidth: '50px' }}>
                            {formatOdds(bet.odds)}
                          </span>
                          <span className="table-cell-meta" style={{ fontSize: '11px', flex: 1 }}>
                            {bet.outcome_name}
                            {bet.point !== undefined && ` (${bet.point > 0 ? '+' : ''}${bet.point})`}
                          </span>
                        </div>
                        {safeLink ? (
                          userTier === 'free' ? (
                            <a
                              href="/account/billing"
                              className="table-bet-action-inline locked"
                              onClick={(e) => e.stopPropagation()}
                              title="Upgrade to Gold or Platinum for 1-click betting"
                            >
                              🔒
                            </a>
                          ) : (
                            <a
                              href={safeLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="table-bet-action-inline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Bet
                            </a>
                          )
                        ) : (
                          <span className="table-bet-action-inline disabled">N/A</span>
                        )}
                      </div>
                    );
                    })}
                  </div>
                </td>

                {/* BET SIZE */}
                <td>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {betAmounts.map((amount, index) => (
                      <div key={`bet-amount-${opportunity.id}-${index}`} className="table-cell-odds">
                        ${formatBetAmount(amount)}
                      </div>
                    ))}
                  </div>
                </td>

                  {/* PROFIT */}
                  <td className="table-cell-profit">
                    ${profit.toFixed(2)}
                  </td>
                </tr>

                {/* Expandable Row */}
                {isExpanded && opportunity.all_bookmaker_odds && (() => {
                  // Get all unique bookmakers across all outcomes
                  const allBookmakerKeys = new Set<string>();
                  Object.values(opportunity.all_bookmaker_odds).forEach(bookmakerOdds => {
                    bookmakerOdds.forEach(bm => allBookmakerKeys.add(bm.bookmaker_key));
                  });
                  const bookmakerList = Array.from(allBookmakerKeys);

                  // Create a map for quick lookups
                  const oddsMap = new Map<string, Map<string, { odds: number; link?: string; bookmaker_link?: string }>>();
                  Object.entries(opportunity.all_bookmaker_odds).forEach(([outcomeName, bookmakerOdds]) => {
                    const outcomeMap = new Map<string, { odds: number; link?: string; bookmaker_link?: string }>();
                    bookmakerOdds.forEach(bm => {
                      outcomeMap.set(bm.bookmaker_key, { odds: bm.odds, link: bm.link, bookmaker_link: bm.bookmaker_link });
                    });
                    oddsMap.set(outcomeName, outcomeMap);
                  });

                  // Calculate average odds for each outcome
                  const avgOdds = new Map<string, number>();
                  Object.entries(opportunity.all_bookmaker_odds).forEach(([outcomeName, bookmakerOdds]) => {
                    const sum = bookmakerOdds.reduce((acc, bm) => acc + bm.odds, 0);
                    avgOdds.set(outcomeName, sum / bookmakerOdds.length);
                  });

                  // Get best odds for each outcome
                  const bestOdds = new Map<string, number>();
                  opportunity.best_odds.forEach(bo => {
                    bestOdds.set(bo.outcome_name, bo.odds);
                  });

                  // Get outcome names
                  const outcomeNames = Object.keys(opportunity.all_bookmaker_odds);

                  return (
                    <tr key={`${opportunity.id}-expanded`} className="expanded-content-row">
                      <td colSpan={7} style={{ padding: '0', backgroundColor: '#0a141d' }}>
                        <div className="odds-table-container">
                          <table className="odds-comparison-table">
                            <thead>
                              <tr className="odds-table-header-row">
                                <th className="odds-table-outcome-header"></th>
                                <th className="odds-table-best-header">Best Odds</th>
                                <th className="odds-table-avg-header">Avg Odds</th>
                                {bookmakerList.map(bookmakerKey => (
                                  <th key={bookmakerKey} className="odds-table-bookmaker-header">
                                    <div className="odds-table-bookmaker-logo">
                                      <BookmakerLogo
                                        bookmakerKey={bookmakerKey}
                                        bookmakerTitle={bookmakerKey}
                                        size="small"
                                      />
                                    </div>
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {outcomeNames.map((outcomeName, idx) => {
                                const avgOdd = avgOdds.get(outcomeName) || 0;
                                const bestOdd = bestOdds.get(outcomeName) || 0;
                                const outcomeData = opportunity.all_bookmaker_odds![outcomeName][0];
                                const pointLabel = outcomeData?.point !== undefined
                                  ? ` (${outcomeData.point > 0 ? '+' : ''}${outcomeData.point})`
                                  : '';

                                return (
                                  <tr key={outcomeName} className={`odds-table-outcome-row ${idx > 0 ? 'no-top-border' : ''}`}>
                                    <td className="odds-table-outcome-name">
                                      {outcomeName}{pointLabel}
                                    </td>
                                    <td className="odds-table-best-cell">
                                      <button className="odds-button odds-button-best">
                                        {formatOdds(bestOdd)}
                                      </button>
                                    </td>
                                    <td className="odds-table-avg-cell">
                                      <button className="odds-button odds-button-avg">
                                        {formatOdds(avgOdd)}
                                      </button>
                                    </td>
                                    {bookmakerList.map(bookmakerKey => {
                                      const outcomeMap = oddsMap.get(outcomeName);
                                      const bookmakerData = outcomeMap?.get(bookmakerKey);

                                      if (!bookmakerData) {
                                        return <td key={bookmakerKey} className="odds-table-bookmaker-cell">-</td>;
                                      }

                                      const isAboveAvg = bookmakerData.odds > avgOdd;
                                      const oddClass = isAboveAvg ? 'odds-button-above-avg' : 'odds-button-below-avg';

                                      const bookmakerLink = getSafeExternalUrl(bookmakerData.link);

                                      return (
                                        <td key={bookmakerKey} className="odds-table-bookmaker-cell">
                                          <div className="odds-cell-content">
                                            {bookmakerLink ? (
                                              <a
                                                href={bookmakerLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={`odds-button ${oddClass}`}
                                              >
                                                {formatOdds(bookmakerData.odds)}
                                              </a>
                                            ) : (
                                              <button className={`odds-button ${oddClass}`}>
                                                {formatOdds(bookmakerData.odds)}
                                              </button>
                                            )}
                                            {bookmakerLink && (
                                              userTier === 'free' ? (
                                                <a
                                                  href="/account/billing"
                                                  className="odds-link-button locked"
                                                  title="Upgrade to Gold or Platinum for 1-click betting"
                                                >
                                                  🔒
                                                </a>
                                              ) : (
                                                <a
                                                  href={bookmakerLink}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="odds-link-button"
                                                >
                                                  Bet
                                                </a>
                                              )
                                            )}
                                          </div>
                                        </td>
                                      );
                                    })}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  );
                })()}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
